import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Truck, User, Info } from "lucide-react";
import { format, isSameDay, addHours } from "date-fns";
import { Location, Order, TractorCapability, WorkOrder, DriverDayOff, Customer } from "@/types/tms";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { viewerTzAbbr } from "@/lib/order-time";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { OrderWithRelations } from "@/components/schedule/types";
import { DroppableAssetTimelineRow } from "@/components/schedule/DroppableAssetTimelineRow";
import { OrderEditDialog } from "@/components/orders/OrderEditDialog";
import { WorkOrderDialog } from "@/components/schedule/WorkOrderDialog";
import { getOrderCardClass } from "@/components/schedule/orderCardUtils";
import { getTractorLocationAtTime } from "@/hooks/useTractorCurrentLocation";
import { AssetScheduleFilters } from "@/components/schedule/AssetScheduleFilters";
import { useWeekNavigation, getDefaultScheduleRange } from "@/hooks/useWeekNavigation";
import { useTurnTime } from "@/hooks/useTurnTime";
import { TractorWithHome, DriverWithHome, OrderDragData, ActiveDragState } from "@/types/resources";
import { ScheduleHeader, ScheduleActions } from "@/components/schedule/ScheduleHeader";
import { useFleetSchedulerAssignment } from "@/hooks/useFleetSchedulerAssignment";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TractorDetailSheet } from "@/components/tractors/TractorDetailSheet";
import { OrderDetailSheet } from "@/components/orders/OrderDetailSheet";
import { useNavigate } from "react-router-dom";
import { useStarredOrderIds } from "@/hooks/useOrderStars";
import { useTenant } from "@/hooks/useTenant";
import { PROD_TENANT_SLUG } from "@/lib/constants";
import { useUrlFilterState, useUrlArrayFilterState } from "@/hooks/useUrlFilterState";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  mapAndHydrateLegRows,
} from "@/lib/orders-with-legs";
import { useTractorOosStatusMap } from "@/hooks/useTractorOosStatusMap";

export default function AssetSchedule({
  embedded = false,
  onActionsReady,
  onTitleChange,
  onTitleExtraReady,
}: {
  embedded?: boolean;
  onActionsReady?: (actions: React.ReactNode) => void;
  onTitleChange?: (suffix: string) => void;
  onTitleExtraReady?: (extra: React.ReactNode) => void;
}) {
  const queryClient = useQueryClient();
  const viewerTz = useViewerTimezone();
  const [dateFilter, setDateFilter] = useState<{ from: Date; to: Date } | null>(() => getDefaultScheduleRange());
  const { weekStart, weekEnd, weekDays, formatWeekRange } = useWeekNavigation(dateFilter);
  const { turnTimeMinutes, handleTurnTimeChange } = useTurnTime();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [previewOrder, setPreviewOrder] = useState<OrderWithRelations | null>(null);
  const navigate = useNavigate();
  const [activeDragState, setActiveDragState] = useState<ActiveDragState | null>(null);
  const [isWorkOrderDialogOpen, setIsWorkOrderDialogOpen] = useState(false);
  const [selectedTractorForWorkOrder, setSelectedTractorForWorkOrder] = useState<TractorWithHome | null>(null);
  const [selectedTractorForPreview, setSelectedTractorForPreview] = useState<TractorWithHome | null>(null);

  const { activeTenant } = useTenant();
  const isProd = activeTenant === PROD_TENANT_SLUG;

  // Filter state (URL-persisted to mirror the Orders page)
  const [searchQuery, setSearchQuery] = useUrlFilterState<string>("q", "");
  const [selectedCapabilities, setSelectedCapabilities] = useState<TractorCapability[]>([]);
  const [statusFilter, setStatusFilter] = useUrlArrayFilterState("status");
  const [customerFilter, setCustomerFilter] = useUrlArrayFilterState("customer");
  const [orderTypeFilter, setOrderTypeFilter] = useUrlFilterState<string>("orderType", "all");
  const [tripTypeFilter, setTripTypeFilter] = useUrlArrayFilterState("tripType");
  const [shipFromFilter, setShipFromFilter] = useUrlArrayFilterState("shipFrom");
  const [shipToFilter, setShipToFilter] = useUrlArrayFilterState("shipTo");
  const [subscribedOnly, setSubscribedOnly] = useState<boolean>(false);

  const { data: starredOrderIds } = useStarredOrderIds();

  // OOS data for tractor rows.
  const { statusById: oosStatusById, recordsById: oosRecordsById } =
    useTractorOosStatusMap();

  // Horizontal scroll sync between the sticky day header and the body grid.
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const syncingRef = useRef(false);
  const handleHeaderScroll = useCallback(() => {
    if (syncingRef.current) return;
    if (!headerScrollRef.current || !bodyScrollRef.current) return;
    syncingRef.current = true;
    bodyScrollRef.current.scrollLeft = headerScrollRef.current.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  }, []);
  const handleBodyScroll = useCallback(() => {
    if (syncingRef.current) return;
    if (!headerScrollRef.current || !bodyScrollRef.current) return;
    syncingRef.current = true;
    headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  }, []);

  // Constrain the body scroll container so rows scroll vertically inside
  // it instead of scrolling the entire page past the schedule.
  useEffect(() => {
    const el = bodyScrollRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 16;
      el.style.maxHeight = `${Math.max(available, 200)}px`;
    };
    update();
    window.addEventListener("resize", update);
    const id = window.setInterval(update, 500);
    return () => {
      window.removeEventListener("resize", update);
      window.clearInterval(id);
    };
  }, []);
  
  // Weekly assignment
  const { assignPlus: assign, isAssigningPlus: isAssigning, clear, isClearing } = useFleetSchedulerAssignment(weekStart, turnTimeMinutes);
  
  // Configure drag sensors - require 8px movement before drag starts to prevent click flash
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const { data: locations } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("archived", false)
        .order("short_code");
      if (error) throw error;
      return data as Location[];
    },
  });

  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("organization_name");
      if (error) throw error;
      return data as Customer[];
    },
  });

  const { data: tractors } = useQuery({
    queryKey: ["tractors-with-home-base"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractors")
        .select(`
          *,
          home_base:locations(*)
        `)
        .order("name");
      if (error) throw error;
      return data as TractorWithHome[];
    },
  });

  const { data: orders } = useQuery({
    queryKey: ["schedule-orders", weekStart.toISOString()],
    queryFn: async () => {
      // Phase 4: per-leg rows so multi-leg orders fan out across the asset timeline.
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .gte("ready_time", weekStart.toISOString())
        .lt("ready_time", weekEnd.toISOString())
        .order("ready_time")
        .limit(3000);
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
  });

  // Fetch work orders from the new table
  const { data: workOrders } = useQuery({
    queryKey: ["work-orders", weekStart.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_orders")
        .select("*")
        .gte("start_time", weekStart.toISOString())
        .lt("start_time", weekEnd.toISOString())
        .order("start_time");
      if (error) throw error;
      return data as WorkOrder[];
    },
  });

  // Fetch driver days off for the week
  const { data: daysOff } = useQuery({
    queryKey: ["driver-days-off", weekStart.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("driver_days_off")
        .select("*")
        .gte("date", format(weekStart, "yyyy-MM-dd"))
        .lt("date", format(weekEnd, "yyyy-MM-dd"));
      if (error) throw error;
      return data as DriverDayOff[];
    },
  });

  // Fetch drivers with home base and flight for auto-assignment
  const { data: drivers } = useQuery({
    queryKey: ["drivers-with-flights"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select(`
          *,
          home_base:locations(*),
          flight:flights(*)
        `)
        .order("name");
      if (error) throw error;
      return data as DriverWithHome[];
    },
  });

  // Mutation to update tractor assignment with optimistic updates
  const updateTractorAssignment = useMutation({
    mutationFn: async ({
      orderId,
      newTractorId,
      newTractorName,
      oldTractorName,
      movedOrder,
      ordersToDisconnect,
    }: {
      orderId: string;
      newTractorId: string | null;
      newTractorName: string;
      oldTractorName: string | null;
      movedOrder: OrderWithRelations;
      ordersToDisconnect: string[];
    }) => {
      // Update the order
      const { error } = await supabase
        .from("orders")
        .update({ assigned_tractor_id: newTractorId })
        .eq("id", orderId);
      if (error) throw error;

      // Log the change
      const { error: logError } = await supabase.from("order_change_log").insert({
        order_id: orderId,
        field_name: "assigned_tractor_id",
        old_value: oldTractorName,
        new_value: newTractorName,
        changed_by: "System",
      });
      if (logError) console.error("Failed to log change:", logError);

      // Disconnect subsequent orders that don't start from where the truck now stops
      if (ordersToDisconnect.length > 0) {
        const { error: disconnectError } = await supabase
          .from("orders")
          .update({ assigned_tractor_id: null })
          .in("id", ordersToDisconnect);
        if (disconnectError) console.error("Failed to disconnect orders:", disconnectError);

        // Log disconnections
        for (const disconnectOrderId of ordersToDisconnect) {
          await supabase.from("order_change_log").insert({
            order_id: disconnectOrderId,
            field_name: "assigned_tractor_id",
            old_value: newTractorName,
            new_value: null,
            changed_by: "System (cascade disconnect)",
          });
        }
      }
    },
    onMutate: async ({ orderId, newTractorId, newTractorName, ordersToDisconnect }) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ["schedule-orders", weekStart.toISOString()] });

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData<OrderWithRelations[]>(["schedule-orders", weekStart.toISOString()]);

      // Optimistically update the cache - update moved order and disconnect cascade orders
      queryClient.setQueryData<OrderWithRelations[]>(
        ["schedule-orders", weekStart.toISOString()],
        (old) =>
          old?.map((order) => {
            if (order.id === orderId) {
              return {
                ...order,
                assigned_tractor_id: newTractorId,
                assigned_tractor: newTractorId
                  ? ({ ...order.assigned_tractor, id: newTractorId, name: newTractorName } as OrderWithRelations["assigned_tractor"])
                  : null,
              };
            }
            if (ordersToDisconnect.includes(order.id)) {
              return {
                ...order,
                assigned_tractor_id: null,
                assigned_tractor: null,
              };
            }
            return order;
          })
      );

      // Return context with snapshotted value for rollback
      return { previousOrders };
    },
    onError: (error: Error, _variables, context) => {
      // Rollback to previous state on error
      if (context?.previousOrders) {
        queryClient.setQueryData(["schedule-orders", weekStart.toISOString()], context.previousOrders);
      }
      toast.error(error.message);
    },
    onSettled: () => {
      // Refetch to ensure server/client sync
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
    },
    onSuccess: () => {
      toast.success("Tractor assignment updated");
    },
  });

  // Helpers mirroring the Orders page filter semantics. Multi-leg = parent
  // order has more than one leg row in the fetched stream.
  const isActiveArr = (v: string[] | undefined) =>
    !!v && v.length > 0 && v.some((x) => x && x !== "all");

  const multiLegParentIds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of orders ?? []) {
      const parentId = (o as any).parent_order_id ?? o.id;
      counts.set(parentId, (counts.get(parentId) ?? 0) + 1);
    }
    const set = new Set<string>();
    counts.forEach((n, id) => { if (n > 1) set.add(id); });
    return set;
  }, [orders]);

  const locationById = useMemo(() => {
    const map = new Map<string, Location>();
    for (const l of locations ?? []) map.set(l.id, l);
    return map;
  }, [locations]);

  const matchesLocation = (
    selectedIds: string[],
    addr: string | null | undefined,
    city: string | null | undefined,
  ) => {
    const a = (addr ?? "").trim().toLowerCase();
    const c = (city ?? "").trim().toLowerCase();
    return selectedIds.some((id) => {
      const loc = locationById.get(id);
      if (!loc) return false;
      const la = (loc.street_address ?? "").trim().toLowerCase();
      const lc = (loc.city ?? "").trim().toLowerCase();
      return la === a && lc === c;
    });
  };

  // Apply all filters to the order stream once (mirrors Orders page logic,
  // minus Tender / BOL which are intentionally excluded from Schedule).
  const visibleOrders = useMemo(() => {
    if (!orders) return [] as OrderWithRelations[];
    const statusActive = isActiveArr(statusFilter);
    const customerActive = isActiveArr(customerFilter);
    const tripActive = isActiveArr(tripTypeFilter);
    const fromActive = isActiveArr(shipFromFilter);
    const toActive = isActiveArr(shipToFilter);
    const orderTypeActive = orderTypeFilter && orderTypeFilter !== "all";

    return orders.filter((o) => {
      if (statusActive && !statusFilter.includes(o.status as string)) return false;
      if (customerActive && !customerFilter.includes((o as any).billing_party_id ?? "")) return false;
      if (tripActive && !tripTypeFilter.includes(o.trip_type as string)) return false;
      if (orderTypeActive) {
        const parentId = (o as any).parent_order_id ?? o.id;
        const isMulti = multiLegParentIds.has(parentId);
        if (orderTypeFilter === "multi_leg" && !isMulti) return false;
        if (orderTypeFilter === "single_leg" && isMulti) return false;
      }
      if (fromActive && !matchesLocation(shipFromFilter, (o as any).ship_from_street_address, o.ship_from_city)) return false;
      if (toActive && !matchesLocation(shipToFilter, (o as any).ship_to_street_address, o.ship_to_city)) return false;
      if (subscribedOnly) {
        const parentId = (o as any).parent_order_id ?? o.id;
        if (!starredOrderIds?.has(parentId) && !starredOrderIds?.has(o.id)) return false;
      }
      return true;
    });
  }, [orders, statusFilter, customerFilter, tripTypeFilter, orderTypeFilter, shipFromFilter, shipToFilter, subscribedOnly, starredOrderIds, multiLegParentIds, locationById]);

  // Get orders assigned to a specific tractor
  const getOrdersForTractor = (tractorId: string) =>
    visibleOrders.filter((order) => order.assigned_tractor_id === tractorId) || [];

  // Get work orders assigned to a specific tractor
  const getWorkOrdersForTractor = (tractorId: string) =>
    workOrders?.filter((wo) => wo.tractor_id === tractorId) || [];

  // Calculate unassigned count for button display
  const unassignedCount = orders?.filter(
    (o) =>
      o.status !== "canceled" &&
      o.status !== "in_transit" &&
      o.status !== "delivered" &&
      !o.assigned_tractor_id &&
      !o.assigned_operator_id &&
      !o.is_work_order
  ).length || 0;

  const assignedCount = orders?.filter(
    (o) => (o.assigned_tractor_id || o.assigned_operator_id) && !o.is_work_order
  ).length || 0;

  const handleWeeklyAssign = () => {
    if (!orders || !tractors || !drivers) return;
    assign({ orders, tractors, drivers, workOrders: workOrders || [], daysOff: daysOff || [] });
  };

  const handleWeeklyClear = () => {
    if (!orders || !tractors || !drivers) return;
    if (confirm(`Clear assignments from ${assignedCount} orders?`)) {
      clear({ orders, tractors, drivers });
    }
  };

  // Filter tractors based on search, capabilities, and orders
  const filteredTractors = useMemo(() => {
    if (!tractors) return [];
    const query = searchQuery.toLowerCase();

    // When any order-level filter is active, hide tractors with no matching orders.
    const orderFiltersActive =
      isActiveArr(statusFilter) ||
      isActiveArr(customerFilter) ||
      isActiveArr(tripTypeFilter) ||
      isActiveArr(shipFromFilter) ||
      isActiveArr(shipToFilter) ||
      (orderTypeFilter && orderTypeFilter !== "all") ||
      subscribedOnly;

    return tractors.filter((tractor) => {
      // Capability filter
      if (selectedCapabilities.length > 0) {
        const hasMatchingCapability = tractor.capabilities.some((cap) =>
          selectedCapabilities.includes(cap as TractorCapability)
        );
        if (!hasMatchingCapability) return false;
      }

      // Unified search: match tractor name OR order/work-order IDs
      if (query) {
        const nameMatch = tractor.name.toLowerCase().includes(query);
        const tractorOrders = getOrdersForTractor(tractor.id);
        const tractorWorkOrders = getWorkOrdersForTractor(tractor.id);
        const orderMatch = tractorOrders.some((order) =>
          order.unique_id.toLowerCase().includes(query)
        );
        const workOrderMatch = tractorWorkOrders.some((wo) =>
          wo.unique_id.toLowerCase().includes(query)
        );
        if (!nameMatch && !orderMatch && !workOrderMatch) return false;
      }

      // Hide tractors with no matching orders when order filters are active.
      // Work orders keep the row visible since they aren't subject to order filters.
      if (orderFiltersActive) {
        const hasOrders = getOrdersForTractor(tractor.id).length > 0;
        const hasWorkOrders = getWorkOrdersForTractor(tractor.id).length > 0;
        if (!hasOrders && !hasWorkOrders) return false;
      }

      return true;
    });
  }, [
    tractors,
    searchQuery,
    selectedCapabilities,
    orders,
    workOrders,
    visibleOrders,
    statusFilter,
    customerFilter,
    tripTypeFilter,
    shipFromFilter,
    shipToFilter,
    orderTypeFilter,
    subscribedOnly,
  ]);

  // Compute capability counts from all tractors (unfiltered)
  const { capabilityCounts, totalCount } = useMemo(() => {
    if (!tractors) return { capabilityCounts: {} as Record<string, number>, totalCount: 0 };
    const counts: Record<string, number> = {};
    for (const t of tractors) {
      for (const cap of t.capabilities) {
        counts[cap] = (counts[cap] || 0) + 1;
      }
    }
    return { capabilityCounts: counts, totalCount: tractors.length };
  }, [tractors]);

  // Sort tractors flat (capability priority, then name)
  const sortedTractors = useMemo(() => {
    const capabilityPriority = { NVO: 0, VO: 1, Manual: 2 } as const;
    const getHighestPriority = (caps: string[]) => {
      if (!caps || caps.length === 0) return 3;
      return Math.min(...caps.map((c) => capabilityPriority[c as keyof typeof capabilityPriority] ?? 3));
    };
    return [...filteredTractors].sort((a, b) => {
      const pa = getHighestPriority(a.capabilities);
      const pb = getHighestPriority(b.capabilities);
      if (pa !== pb) return pa - pb;
      return a.name.localeCompare(b.name);
    });
  }, [filteredTractors]);

  const handleOrderClick = async (order: OrderWithRelations) => {
    // Open the preview panel; full edit happens from the preview's edit action.
    setPreviewOrder(order);
  };

  const handleCreateWorkOrder = (tractor: TractorWithHome) => {
    setSelectedTractorForWorkOrder(tractor);
    setIsWorkOrderDialogOpen(true);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as OrderDragData | undefined;
    if (data?.type === "order") {
      const rect = event.active.rect.current.initial;
      setActiveDragState({
        data,
        width: rect?.width || 80,
        height: rect?.height || 40,
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragState(null);
    const { active, over } = event;

    if (!over || !active.data.current) return;

    const dragData = active.data.current as OrderDragData;
    const dropData = over.data.current as { type: string; tractorId: string; tractorName: string } | undefined;

    if (!dropData || dropData.type !== "tractor") return;

    const order = dragData.order;
    const newTractorId = dropData.tractorId;

    // Drop on the synthetic "Unassigned" row → clear assignment
    if (newTractorId === "unassigned") {
      if (!order.assigned_tractor_id) return;
      updateTractorAssignment.mutate({
        orderId: order.parent_order_id ?? order.id,
        newTractorId: null,
        newTractorName: "Unassigned",
        oldTractorName: order.assigned_tractor?.name || null,
        movedOrder: order,
        ordersToDisconnect: [],
      });
      return;
    }

    // Don't update if dropping on the same tractor
    if (order.assigned_tractor_id === newTractorId) return;

    const orderStart = new Date(order.ready_time);
    const orderEnd = new Date(order.requested_delivery_time_end ?? order.requested_delivery_time);

    // Check if the tractor is at the order's start location
    const targetTractor = tractors?.find((t) => t.id === newTractorId);
    if (targetTractor) {
      // Get orders for this tractor excluding the order being moved
      const tractorOrdersForLocation = (orders || []).filter(
        (o) => o.assigned_tractor_id === newTractorId && o.id !== order.id
      );
      const tractorLocationAtOrderStart = getTractorLocationAtTime(
        targetTractor,
        tractorOrdersForLocation,
        orderStart
      );
      const orderOrigin = order.ship_from_city.toLowerCase().trim();
      const tractorLocation = tractorLocationAtOrderStart?.toLowerCase().trim() || "";

      if (tractorLocation !== orderOrigin) {
        toast.error(
          `Cannot assign - tractor will be at ${tractorLocationAtOrderStart || "unknown"}, but order starts from ${order.ship_from_city}`
        );
        return;
      }
    }

    // Check for work order conflicts
    const tractorWorkOrders = getWorkOrdersForTractor(newTractorId);
    const hasWorkOrderConflict = tractorWorkOrders.some((wo) => {
      const woStart = new Date(wo.start_time);
      const woEnd = addHours(woStart, wo.duration_hours);
      return orderStart < woEnd && orderEnd > woStart;
    });

    if (hasWorkOrderConflict) {
      toast.error("Cannot assign to this tractor - it has a conflicting work order");
      return;
    }

    // Check for existing order conflicts
    const tractorOrders = getOrdersForTractor(newTractorId);
    const hasOrderConflict = tractorOrders.some((existingOrder) => {
      // Skip the order being dragged
      if (existingOrder.id === order.id) return false;
      const existingStart = new Date(existingOrder.ready_time);
      const existingEnd = new Date(existingOrder.requested_delivery_time_end ?? existingOrder.requested_delivery_time);
      return orderStart < existingEnd && orderEnd > existingStart;
    });

    if (hasOrderConflict) {
      toast.error("Cannot assign to this tractor - it has a conflicting order");
      return;
    }

    // Find subsequent orders on the NEW tractor that need to be disconnected
    const movedOrderDestination = order.ship_to_city.toLowerCase().trim();
    const newTractorSubsequent = tractorOrders
      .filter((o) => {
        if (o.id === order.id) return false;
        return new Date(o.ready_time) >= orderEnd;
      })
      .sort((a, b) => new Date(a.ready_time).getTime() - new Date(b.ready_time).getTime());

    const ordersToDisconnect: string[] = [];
    for (const sub of newTractorSubsequent) {
      if (sub.ship_from_city.toLowerCase().trim() !== movedOrderDestination) {
        // Cards are per-leg; disconnect the parent order row.
        ordersToDisconnect.push(sub.parent_order_id ?? sub.id);
      } else {
        break;
      }
    }

    // Find subsequent orders on the SOURCE tractor that need to be disconnected
    // After removing this order, the source tractor stays at the previous order's destination
    // (or home base if this was the first order). Subsequent orders expecting the truck
    // at the dragged order's ship_from_city need to be checked.
    const sourceTractorId = order.assigned_tractor_id;
    if (sourceTractorId) {
      const sourceOrders = (orders || [])
        .filter((o) => o.assigned_tractor_id === sourceTractorId && o.id !== order.id)
        .sort((a, b) => new Date(a.ready_time).getTime() - new Date(b.ready_time).getTime());

      // Find where the source tractor will be after the dragged order is removed
      // It's the ship_to_city of the last order before the dragged order, or home base
      const sourceTractor = tractors?.find((t) => t.id === sourceTractorId);
      const ordersBefore = sourceOrders.filter(
        (o) => new Date(o.requested_delivery_time_end ?? o.requested_delivery_time) <= orderStart
      );
      const sourceTruckLocation = ordersBefore.length > 0
        ? ordersBefore[ordersBefore.length - 1].ship_to_city.toLowerCase().trim()
        : (sourceTractor?.home_base?.city?.toLowerCase().trim() || "");

      // Check orders after the dragged order on the source tractor
      const sourceSubsequent = sourceOrders.filter(
        (o) => new Date(o.ready_time) >= orderStart
      );

      for (const sub of sourceSubsequent) {
        if (sub.ship_from_city.toLowerCase().trim() !== sourceTruckLocation) {
          ordersToDisconnect.push(sub.parent_order_id ?? sub.id);
        } else {
          break;
        }
      }
    }

    if (ordersToDisconnect.length > 0) {
      toast.info(`${ordersToDisconnect.length} subsequent order(s) will be unassigned due to location mismatch`);
    }

    updateTractorAssignment.mutate({
      // Phase 4: cards are per-leg now; persist tractor on the parent order row.
      orderId: order.parent_order_id ?? order.id,
      newTractorId,
      newTractorName: dropData.tractorName,
      oldTractorName: order.assigned_tractor?.name || null,
      movedOrder: order,
      ordersToDisconnect,
    });
  };

  /* BETA: Assign/Clear/Turn actions hidden */
  const actionsEl = null;

  useEffect(() => {
    if (embedded && onActionsReady) {
      onActionsReady(actionsEl);
    }
  });

  useEffect(() => {
    if (embedded && onTitleChange) {
      onTitleChange(formatWeekRange());
    }
  }, [embedded, onTitleChange, formatWeekRange]);

  const legendButton = (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Legend">
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto">
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-dashed border-foreground rounded bg-transparent" />
            <span className="text-muted-foreground">Booked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[hsl(270_60%_60%/0.2)] border border-[hsl(270_60%_60%)] rounded" />
            <span className="text-muted-foreground">Confirmed</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 border border-[hsl(270_60%_60%)] rounded"
              style={{
                background: `repeating-linear-gradient(
                  45deg,
                  hsl(var(--destructive) / 0.3),
                  hsl(var(--destructive) / 0.3) 2px,
                  hsl(270 60% 60% / 0.15) 2px,
                  hsl(270 60% 60% / 0.15) 4px
                )`,
              }}
            />
            <span className="text-muted-foreground">Confirmed (no driver)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[hsl(var(--status-confirmed)/0.2)] border border-[hsl(var(--status-confirmed))] rounded" />
            <span className="text-muted-foreground">In Transit</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[hsl(var(--status-delivered)/0.2)] border border-[hsl(var(--status-delivered))] rounded" />
            <span className="text-muted-foreground">Delivered</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-dashed border-[hsl(var(--status-canceled))] rounded bg-transparent" />
            <span className="text-muted-foreground">Canceled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 bg-muted border rounded" />
            <span className="text-muted-foreground">At non-home location</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  useEffect(() => {
    if (embedded && onTitleExtraReady) {
      onTitleExtraReady(legendButton);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  // Synthetic "Unassigned" tractor row used at the top of the schedule
  const unassignedOrders = useMemo(
    () =>
      visibleOrders.filter(
        (o) =>
          !o.assigned_tractor_id &&
          !o.is_work_order &&
          o.status !== "canceled" &&
          o.status !== "delivered",
      ),
    [visibleOrders],
  );
  const unassignedTractor = useMemo(
    () =>
      ({
        id: "unassigned",
        name: "Unassigned",
        capabilities: [] as TractorCapability[],
        home_base: null,
        home_base_id: null,
      }) as unknown as TractorWithHome,
    [],
  );

  const DAY_COL_WIDTH = 180;
  const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21];

  const scheduleHeaderEl = (
    <ScheduleHeader
      turnTimeMinutes={turnTimeMinutes}
      onTurnTimeChange={handleTurnTimeChange}
      unassignedCount={unassignedCount}
      assignedCount={assignedCount}
      onAssign={handleWeeklyAssign}
      onClear={handleWeeklyClear}
      isAssigning={isAssigning}
      isClearing={isClearing}
      idPrefix="asset"
      hideActions={embedded}
    />
  );

  const content = (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="animate-fade-in">
          {/* Sticky filter bar + day header */}
          <div className="sticky top-0 z-30 bg-background -mx-6 px-6 pt-2 space-y-4">
          <AssetScheduleFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            customerFilter={customerFilter}
            onCustomerFilterChange={setCustomerFilter}
            orderTypeFilter={orderTypeFilter}
            onOrderTypeFilterChange={setOrderTypeFilter}
            tripTypeFilter={tripTypeFilter}
            onTripTypeFilterChange={setTripTypeFilter}
            shipFromFilter={shipFromFilter}
            onShipFromFilterChange={setShipFromFilter}
            shipToFilter={shipToFilter}
            onShipToFilterChange={setShipToFilter}
            locations={locations ?? []}
            customers={customers ?? []}
            isProd={isProd}
            subscribedOnly={subscribedOnly}
            onSubscribedOnlyChange={setSubscribedOnly}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
          />

            {/* Day/date header - synced horizontally with body below */}
            <div
              ref={headerScrollRef}
              onScroll={handleHeaderScroll}
              className="overflow-x-auto border rounded-t-lg bg-card scrollbar-none [&::-webkit-scrollbar]:hidden"
            >
              <div
                style={{ minWidth: `${200 + Math.max(weekDays.length, 1) * DAY_COL_WIDTH}px` }}
                className="w-full flex"
              >
                <div className="w-[200px] shrink-0 p-3 bg-card border-r sticky left-0 z-30" />
                <div className="flex-1 flex">
                  {weekDays.map((day) => {
                    const isToday = isSameDay(day, new Date());
                    return (
                      <div
                        key={day.toISOString()}
                        className={cn(
                          "flex-1 min-w-[180px] px-3 pt-3 pb-2 text-center border-r last:border-r-0",
                          isToday && "bg-row-selected shadow-[inset_0_2px_0_0_hsl(var(--primary))]"
                        )}
                      >
                        <div className="text-xs text-muted-foreground text-center">{format(day, "EEE")}</div>
                        <div
                          className={cn(
                            "text-lg font-medium text-center",
                            isToday && "text-primary"
                          )}
                        >
                          {format(day, "d")}
                        </div>
                        {weekDays.length === 1 && (
                          <div className="mt-1 -mx-3 flex text-[9px] text-muted-foreground/70 font-mono">
                            {HOUR_TICKS.map((h, i) => {
                              const abbr = viewerTzAbbr(day.toISOString(), viewerTz);
                              return (
                                <span key={h} className="flex-1 text-left pl-1">
                                  {String(h).padStart(2, "0")}:00
                                  {i === 0 && abbr ? ` ${abbr}` : ""}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Body scroll container - synced horizontally with the sticky header */}
          <div
            ref={bodyScrollRef}
            onScroll={handleBodyScroll}
            className="overflow-auto border border-t-0 rounded-b-lg bg-card"
          >
            <div
              style={{ minWidth: `${200 + Math.max(weekDays.length, 1) * DAY_COL_WIDTH}px` }}
              className="w-full border-t"
            >
              {sortedTractors.length > 0 ? (
                <>
                  {/* Unassigned synthetic row */}
                  <div className="border-b">
                    <DroppableAssetTimelineRow
                      key="unassigned"
                      tractor={unassignedTractor}
                      orders={unassignedOrders}
                      workOrders={[]}
                      weekStart={weekStart}
                      weekEnd={weekEnd}
                      weekDays={weekDays}
                      onOrderClick={handleOrderClick}
                    />
                  </div>
                  {sortedTractors.map((tractor) => {
                    const tractorOrders = getOrdersForTractor(tractor.id);
                    const tractorWorkOrders: WorkOrder[] = [];
                    const oosStatus = oosStatusById.get(tractor.id);
                    const oosRecords = oosRecordsById.get(tractor.id);
                    const oosWindows = oosRecords
                      ? oosRecords
                          .filter((r) => r.status === "open" || r.status === "updated")
                          .map((r) => ({
                            start: r.window_start ? new Date(r.window_start) : null,
                            end: r.window_end ? new Date(r.window_end) : null,
                          }))
                          .filter((w): w is { start: Date; end: Date | null } =>
                            !!w.start && !Number.isNaN(w.start.getTime()),
                          )
                          .filter(
                            (w) =>
                              w.start.getTime() < weekEnd.getTime() &&
                              (w.end === null || w.end.getTime() > weekStart.getTime()),
                          )
                      : undefined;
                    return (
                      <DroppableAssetTimelineRow
                        key={tractor.id}
                        tractor={tractor}
                        orders={tractorOrders}
                        workOrders={tractorWorkOrders}
                        weekStart={weekStart}
                        weekEnd={weekEnd}
                        weekDays={weekDays}
                        onOrderClick={handleOrderClick}
                        onCreateWorkOrder={handleCreateWorkOrder}
                        onTractorClick={setSelectedTractorForPreview}
                        oosStatus={oosStatus}
                        oosWindows={oosWindows}
                      />
                    );
                  })}
                </>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  {(searchQuery.trim() || selectedCapabilities.length > 0) ? "No tractors match your filter criteria" : "No tractors configured"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {activeDragState && (
            <div
              style={{
                width: activeDragState.width,
                height: activeDragState.height,
              }}
              className={cn(
                "rounded flex items-center justify-center gap-0.5 px-0.5 shadow-lg opacity-90",
                getOrderCardClass(activeDragState.data.order.status)
              )}
            >
              {activeDragState.data.order.status !== "canceled" && activeDragState.data.order.status !== "delivered" && (
                <>
                  {!activeDragState.data.order.assigned_tractor_id && (
                    <Truck className="h-3 w-3 text-destructive shrink-0" />
                  )}
                  {!activeDragState.data.order.assigned_operator_id && (
                    <User className="h-3 w-3 text-destructive shrink-0" />
                  )}
                </>
              )}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Order Edit Dialog */}
      <OrderEditDialog
        order={selectedOrder}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        mode="full"
      />

      {/* Order preview panel */}
      <OrderDetailSheet
        order={previewOrder}
        open={!!previewOrder}
        onOpenChange={(open) => { if (!open) setPreviewOrder(null); }}
        onEdit={async (order) => {
          // Schedule rows come from `orders_with_legs` where `id` is the leg id
          // and `parent_order_id` is the parent. The edit dialog needs the parent.
          const parentId = (order as any).parent_order_id ?? order.id;
          setPreviewOrder(null);
          if (parentId !== order.id) {
            const { data: parent } = await supabase
              .from("orders")
              .select("*, billing_party:customers(*), assigned_tractor:tractors(*), assigned_operator:drivers!assigned_operator_id(*), assigned_operator_2:drivers!assigned_operator_2_id(*)")
              .eq("id", parentId)
              .maybeSingle();
            if (parent) {
              setSelectedOrder(parent as OrderWithRelations);
              setIsEditDialogOpen(true);
              return;
            }
          }
          setSelectedOrder(order as OrderWithRelations);
          setIsEditDialogOpen(true);
        }}
        onView={(order) => {
          const targetId = (order as any).parent_order_id || order.id;
          const legParam = (order as any).parent_order_id ? `?leg=${order.id}` : "";
          setPreviewOrder(null);
          navigate(`/orders/${targetId}${legParam}`);
        }}
      />

      {/* Tractor preview */}
      <TractorDetailSheet
        tractor={selectedTractorForPreview}
        open={!!selectedTractorForPreview}
        onOpenChange={(open) => { if (!open) setSelectedTractorForPreview(null); }}
        onEdit={() => {}}
      />

      {/* Work Order Dialog */}
      <WorkOrderDialog
        open={isWorkOrderDialogOpen}
        onOpenChange={setIsWorkOrderDialogOpen}
        tractor={selectedTractorForWorkOrder}
        tractorCurrentLocation={
          selectedTractorForWorkOrder
            ? getTractorLocationAtTime(
                selectedTractorForWorkOrder,
                getOrdersForTractor(selectedTractorForWorkOrder.id),
                new Date()
              )
            : null
        }
      />
    </>
  );

  if (embedded) {
    return (
      <>
        <div className="mb-4">{scheduleHeaderEl}</div>
        {content}
      </>
    );
  }

  return (
    <AppLayout title="Asset Schedule" actions={scheduleHeaderEl}>
      {content}
    </AppLayout>
  );
}
